import {
  CycleCountSessionItem,
  SessionProgress,
} from '../models/cycle-count-session.model';
import { formatAmountWithSeparators } from './cycle-count-currency.utils';
import { isCycleCountItemCounted } from './cycle-count-filter-sort.utils';

/**
 * Formats a last-counted date string for display in variance report tables and cards.
 * Returns '—' for null, undefined, or empty input so cells always have a visible placeholder.
 * Shows only the date portion (no time) in the "month, day, year" format, e.g.
 * "September 2, 2026" (#31383 — replaced the numeric MM/DD/YYYY form).
 */
export function formatVarianceReportDate(
  dateStr: string | null | undefined
): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** #29187: numeric mm/dd/yyyy date (e.g. 09/07/2026), used for the "posted to Viewpoint" date. */
export function formatDateMmDdYyyy(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formats a date-time string for stamps that need both the date and the time of day
 * (session created-at header, adjustment audit stamp). Uses the same "month, day, year"
 * date as {@link formatVarianceReportDate} followed by the local clock time, e.g.
 * "September 2, 2026, 8:00 AM". Returns '—' for null/undefined/invalid input.
 */
export function formatVarianceReportDateTime(
  dateStr: string | null | undefined
): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Returns counted vs total progress for a session.
 * An item is "counted" when actualQty is not null OR isNotFound is true.
 */
export function computeSessionProgress(
  items: CycleCountSessionItem[]
): SessionProgress {
  const total = items.length;
  const counted = items.filter(
    (i) => i.actualQty !== null || i.isNotFound
  ).length;
  const percentage = total === 0 ? 0 : Math.round((counted / total) * 100);
  return { counted, total, percentage };
}

/**
 * Tallies a session for the pre-submit confirmation dialog (#30620).
 *
 * `counted` and `notFound` are disjoint categories that mirror the server's
 * SubmitSessionResponse exactly (CycleCountSessionRepository: counted =
 * ActualQty.HasValue, notFound = IsNotFound), so the pre-submit "Submit Count"
 * dialog agrees with the post-submit "Session Complete" alert. This is distinct
 * from computeSessionProgress, whose `counted` deliberately includes not-found
 * items because the progress bar measures how many items have been actioned.
 *
 * The prior dialog derived not-found as (total - counted), which collapses to 0
 * once every sub-location is actioned — hiding genuinely not-found items.
 */
export function summarizeSessionSubmission(items: CycleCountSessionItem[]): {
  counted: number;
  notFound: number;
} {
  return {
    counted: items.filter((i) => i.actualQty !== null).length,
    notFound: items.filter((i) => i.isNotFound).length,
  };
}

/**
 * Returns true when every item has been counted (actualQty != null OR isNotFound)
 * and the list has at least one item.
 */
export function isSessionSubmittable(items: CycleCountSessionItem[]): boolean {
  return (
    items.length > 0 && items.every((i) => i.actualQty !== null || i.isNotFound)
  );
}

/**
 * #31334: an admin may delete a line only while it is still uncounted. A counted/Broken/Not-Found item is
 * blocked (the agreed "block if counted" rule) so entered counting work is never silently discarded. The
 * open-vs-closed count check is the server's responsibility (a closed count must be reopened first).
 */
export function canDeleteCycleCountItem(item: CycleCountSessionItem): boolean {
  return !isCycleCountItemCounted(item);
}

/**
 * Formats a unit cost for display in the session item grid.
 * Returns '—' for null/undefined; otherwise formats as '$0.00'.
 */
export function formatUnitCost(value: number | null | undefined): string {
  if (value == null) return '—';
  return `$${formatAmountWithSeparators(value)}`;
}

/**
 * Sorts items alphabetically by subLocationCode then materialId.
 * Returns a new array — does not mutate the original.
 */
export function sortItemsBySubLocation(
  items: CycleCountSessionItem[]
): CycleCountSessionItem[] {
  return [...items].sort((a, b) => {
    const locCmp = a.subLocationCode.localeCompare(b.subLocationCode);
    return locCmp !== 0 ? locCmp : a.materialId.localeCompare(b.materialId);
  });
}
